import { ChangeEvent, FC, useEffect, useRef, useState } from "react"
import { CSVLink } from "react-csv"
import { FaCalendarAlt } from "react-icons/fa"
import { FiInfo } from "react-icons/fi"
import { TiArrowSortedDown, TiArrowSortedUp } from "react-icons/ti"
import { Link } from "react-router-dom"
import { get } from "lodash"

import { useTranslation } from "react-i18next"

import { MultiDatesPicker } from "components/date/MultiDatesPicker"
import { TableColumnsFilter } from "components/dropdowns/TableColumnsFilter"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { HoverTip } from "components/tooltips/HoverTip"

import { TableAction, TableHandler } from "constants/type"

import { GameSessions } from "services/user/type"

import dayjs from "libs/dayjs"

import { ReactComponent as DownloadIcon } from "../../assets/icons/download.svg"
import { tablePageSize } from "../../constants/data"
import { SortOrderBy, SortOrderType, TableDataType } from "../../constants/eums"
import { TableColumnsInterface } from "../../constants/interfaces"
import { formatDateTime, formatTableCell, formatTableCellForPrint } from "../../util/formatter"
import { TableDropdown } from "../dropdowns/TableDropdown"
import { NoData } from "../NoData"
import { TablePagination } from "../paginations/TablePagination"

import { isColumnSortable } from "./isColumnSortable"
import { resolvePaymentMethodCell } from "./payment-method-cell"

export interface ITableProps {
  title: string
  handler: TableHandler
  columns: TableColumnsInterface[]
  onRowClick?: any
  id?: string | number
  filters?: {
    createdAt?: (Date | null)[]
    [key: string]: any
  }
  actions?: TableAction[]
  updatedAt?: any
  buttonHander?: any
  buttonText?: string
  defaultSortOrderBy?: SortOrderBy | string
  defaultSortOrder?: SortOrderType
  isExport?: boolean
  exportFileName?: string
  isColumnsFilterable?: boolean
  gameSessions?: GameSessions[]
  onDatePickerClick?: any
  dateData?: any
  handleDateChange?: (rowId: string, dates: string[]) => void
  handleDropdownChange?: (row: any, column: TableColumnsInterface, newValue: any) => void
}

export const Table: FC<ITableProps> = (props) => {
  const { t } = useTranslation(["translation"], { useSuspense: false })
  const {
    title,
    handler,
    columns,
    onRowClick,
    id,
    filters,
    actions,
    updatedAt,
    buttonHander,
    buttonText,
    defaultSortOrderBy = SortOrderBy.CREATED_AT,
    defaultSortOrder = SortOrderType.DESC,
    isExport = false,
    exportFileName,
    isColumnsFilterable = false,
    gameSessions,
    onDatePickerClick,
    dateData,
    handleDateChange,
    handleDropdownChange
  } = props

  const csvLinkEl = useRef<any>(null)

  const [rows, setRows] = useState<any>([])
  const [total, setTotal] = useState<number>(1)
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)
  const [totalPages, setTotalPages] = useState<number>(1)

  const [orderBy, setOrderBy] = useState<string>(defaultSortOrderBy)
  const [order, setOrder] = useState<string>(defaultSortOrder)
  // SOFT-554 (T09): set when the backend could only re-order the current page by the displayed
  // authoritative balances (cross-page order approximate on microservice tenants). Labels the
  // actively-sorted balance header so operators know the ordering is not globally exact.
  const [approximateOrder, setApproximateOrder] = useState<boolean>(false)
  const [csvData, setCsvData] = useState<any>([])
  const [loadingCsvData, setLoadingCsvData] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  // Distinguishes a failed load from a genuinely empty result. In microservice
  // mode a down FS/NUX authority answers Transactions/Bets with HTTP 503
  // `AUTHORITY_UNAVAILABLE` — that is a transient error to retry, NOT "no rows".
  const [loadError, setLoadError] = useState<null | "unavailable" | "generic">(null)
  const [activeDatePicker, setActiveDatePicker] = useState<string | null>(null)
  const [selectedColumns, setSelectColumns] = useState<TableColumnsInterface[]>(
    columns.map((column) => ({
      ...column,
      selected: true
    }))
  )

  useEffect(() => {
    setSelectColumns(columns.map((column) => ({ ...column, selected: true })))
  }, [columns])

  useEffect(() => {
    onLoad(page, pageSize)
  }, [updatedAt, filters, page, pageSize, orderBy, order, id])

  const onLoad = async (page: number, pageSize: number): Promise<void> => {
    try {
      setLoading(true)
      setLoadError(null)
      const result = await handler(page, pageSize, id, filters, orderBy, order)
      setApproximateOrder(Boolean(result?.approximate_order))
      if ((result?.data ?? []).length > 0) {
        setRows(result?.data)
        setPage(result?.page)
        setTotal(result?.total)
        setTotalPages(result?.total_pages)
        setLoading(false)
        return
      }
      setRows([])
      setPage(1)
      setTotal(1)
      setTotalPages(1)
      setLoading(false)
    } catch (error: any) {
      // The axios interceptor rejects with the response object, so status/code
      // live directly on `error`. A 503 AUTHORITY_UNAVAILABLE is transient —
      // surface a retry affordance rather than the misleading "No data".
      const isUnavailable = error?.status === 503 || error?.data?.code === "AUTHORITY_UNAVAILABLE"
      setRows([])
      setApproximateOrder(false)
      setLoadError(isUnavailable ? "unavailable" : "generic")
      setLoading(false)
    }
  }

  const onJump = (jumpTo: number) => {
    if (!jumpTo) return
    onLoad(jumpTo, pageSize)
  }

  const onPageSizeChange = (value: number): void => {
    setPageSize(value)
  }

  const onAction = (e: any, row: any, action: any): void => {
    e.stopPropagation()
    action(row)
  }

  const handleRequestSort = (value: string): void => {
    if (value === orderBy) {
      return setOrder(order === SortOrderType.ASC ? SortOrderType.DESC : SortOrderType.ASC)
    }
    setOrderBy(value)
    setOrder(SortOrderType.ASC)
  }

  const handleExportAll = () => {
    if (!loadingCsvData) {
      setLoadingCsvData(true)
      handler(1, total, id, filters, orderBy, order)
        .then((result: any) => {
          const csvResult = result.data.map((row: any) =>
            selectedColumns
              .filter((column) => column.selected === true)
              .reduce((result, column) => {
                result[column.title] = formatTableCellForPrint(get(row, column.value), column.format)
                return result
              }, {} as any)
          )
          setCsvData(csvResult)
          setTimeout(() => csvLinkEl.current.link.click())
          setLoadingCsvData(false)
        })
        .catch(() => {
          setLoadingCsvData(false)
          setCsvData([])
        })
    }
  }

  const buildExportFileName = (): string | undefined => {
    if (!exportFileName) return undefined
    const range = Array.isArray(filters?.createdAt) ? filters?.createdAt : []
    const [from, to] = range ?? []
    const fmt = (date: Date) => dayjs(date).format("YYYY-MM-DD")
    let suffix = ""
    if (from && to) suffix = `_${fmt(from)}_to_${fmt(to)}`
    else if (from) suffix = `_from_${fmt(from)}`
    else if (to) suffix = `_until_${fmt(to)}`
    return `${exportFileName}${suffix}.csv`
  }

  const formatTableGameSessions = (rawValue?: GameSessions[], currency?: string) => {
    if (!Array.isArray(rawValue) || !rawValue.length || rawValue.some((item: any) => !item || !item.currency)) {
      return <ul></ul>
    }
    if (currency) {
      const gameSessionForCurrency = rawValue?.find((item: any) => item.currency === currency)
      return <ul>{gameSessionForCurrency?.short_id}</ul>
    }
    return <ul>{rawValue && rawValue.map((item: any, index: number) => <li key={index}>{`${item.currency} - ${item.short_id}`}</li>)}</ul>
  }

  const updateRowField = (id: string, key: string, value: any) => {
    setRows((prev: any) => prev.map((r: any) => (r.id === id ? { ...r, [key]: value } : r)))
  }

  return (
    <div className="grid w-full grid-cols-1 rounded-lg border border-[rgba(0,0,0,0.25)] dark:border-[rgba(255,255,255,0.15)]">
      <div className="z-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between rounded-t-lg py-4 px-6 sm:py-6 sm:px-8 border-b border-[rgba(0,0,0,0.05)] bg-[rgba(212,212,212,0.6)] dark:bg-[rgba(212,212,212,0.1)] backdrop-blur-[9px] !dark:shadow-[0px_3px_6px_0px_rgba(192,192,192,0.6)] dark:border-[rgba(255,255,255,0.1)] dark:text-white">
        <div className="flex items-center justify-start text-2xl sm:text-lg text-black dark:text-white gap-4 ">
          {title}
          {loading ? <span className="loading loading-spinner loading-md text-gray-400" /> : null}
          {buttonHander ? (
            <button className="btn btn-primary btn-sm ml-8" onClick={() => buttonHander()}>
              {buttonText}
            </button>
          ) : null}
        </div>
        {rows?.length ? (
          <div className="flex flex-row-reverse flex-wrap sm:flex-row items-center gap-2">
            {isExport && (
              <>
                <button
                  className="flex items-center justify-center text-center gap-2 rounded-full text-sm px-3 py-1 sm:text-base sm:px-6 sm:py-2 border-b border-[rgba(139,92,246,1)] transition-all text-black dark:text-white bg-[#F5F5F5] border-gray-200 dark:bg-[#0C0C1A] dark:border-[rgba(139,92,246,1)] hover:opacity-80  hover:text-blue-400 active:scale-90"
                  onClick={handleExportAll}
                  disabled={loadingCsvData}
                >
                  {loadingCsvData ? t("table.loadingCsv") : t("table.exportCsv")}
                  <DownloadIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <CSVLink data={csvData} ref={csvLinkEl} filename={buildExportFileName()} />
              </>
            )}
            {isColumnsFilterable && <TableColumnsFilter columns={selectedColumns} setColumns={setSelectColumns} />}
            <div className="flex items-center gap-2 text-sm sm:text-base text-black dark:text-white">
              <label>{t("table.rowsPerPage")}</label>
              <TableDropdown value={pageSize} onValueChange={onPageSizeChange} columns={tablePageSize} />
            </div>
          </div>
        ) : null}
      </div>
      {rows?.length ? (
        <div className="z-0">
          <div className="overflow-x-auto scroll-hidden">
            <table className="z-0 table w-full table-compact no-pin">
              <thead className="border border-[rgba(0,0,0,0.05)] bg-[rgba(212,212,212,0.14)] backdrop-blur-[9px] !dark:shadow-[0px_3px_6px_0px_rgba(192,192,192,0.6)] dark:border-[rgba(255,255,255,0.1)] dark:text-white">
                <tr className="text-[#a2adbb] text-14">
                  {selectedColumns
                    .filter((column) => column.selected === true)
                    .map((column: TableColumnsInterface, key: number) => (
                      <th key={key} className="border-r border-[rgba(255,255,255,0.1)]">
                        <button
                          disabled={!isColumnSortable(column)}
                          className={`${
                            isColumnSortable(column) && "cursor-pointer"
                          } flex justify-center items-center gap-1 w-full text-[rgba(0,0,0,0.7)] dark:text-[rgba(255,255,255,0.46)]`}
                          onClick={() => handleRequestSort(column.value)}
                        >
                          {column.title}
                          {orderBy === column.value && (order === SortOrderType.ASC ? <TiArrowSortedUp /> : <TiArrowSortedDown />)}
                          {approximateOrder && orderBy === column.value && (
                            // Portal tooltip (not native `title` / daisyUI) — 0.5s delay, standard
                            // font, and never clipped by the table wrapper's `overflow`.
                            <HoverTip text={t("table.approximateOrder")}>
                              <FiInfo size={15} className="opacity-60 hover:opacity-100" />
                            </HoverTip>
                          )}
                        </button>
                      </th>
                    ))}
                  {actions && actions.length > 0 && <th>{t("common.actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {(rows || []).map((row: any, key: number) => {
                  const currency = row?.coin
                  return (
                    <tr
                      key={key}
                      onClick={() => onRowClick && onRowClick(row)}
                      className={`${
                        onRowClick && "cursor-pointer"
                      } relative h-[65px] odd:bg-[rgba(212,212,212,0.7)]  even odd:dark:bg-[rgba(255,255,255,0.04)]`}
                    >
                      {selectedColumns
                        .filter((column) => column.selected === true)
                        .map((column: TableColumnsInterface, index: number) => {
                          let rawValue = get(row, column.value)
                          if (column.value === "other.paymentMethod") {
                            rawValue = resolvePaymentMethodCell(row)
                          }

                          return (
                            <td key={index} className="border-r border-[rgba(255,255,255,0.1)] text-[rgba(0,0,0,0.7)] dark:text-white">
                              {column.customRender ? (
                                <div className="justify-self-center">{column.customRender(rawValue, row)}</div>
                              ) : column.format === TableDataType.IMAGE && rawValue ? (
                                <img className="h-10 w-auto max-w-full" src={rawValue} />
                              ) : column.format === TableDataType.FILE ? (
                                <div>{rawValue}</div>
                              ) : column.format === TableDataType.DATE ? (
                                <div className="tooltip" data-tip={formatDateTime(rawValue)}>
                                  {formatTableCell(rawValue, column.format)}
                                </div>
                              ) : column.link ? (
                                <Link to={column.link(rawValue)} className="underline">
                                  {formatTableCell(rawValue, column.format)}
                                </Link>
                              ) : column.format === TableDataType.DATES ? (
                                <div className="flex flex-col gap-1">
                                  {rawValue &&
                                    rawValue.split(",").map((item: any, index: number) => (
                                      <button
                                        className="w-28 btn btn-sm btn-secondary px-2 py-1"
                                        key={index}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {item}
                                      </button>
                                    ))}
                                </div>
                              ) : column.format === TableDataType.JSON && column.value === "gameSessions" ? (
                                formatTableGameSessions(rawValue, currency)
                              ) : column.format === TableDataType.DATE_PICKER ? (
                                <div className="relative">
                                  <button
                                    className="btn btn-circle btn-outline btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      onDatePickerClick(row)
                                      setActiveDatePicker((prev) => (prev === row.id ? null : row.id))
                                    }}
                                  >
                                    <FaCalendarAlt />
                                  </button>
                                  {activeDatePicker === row.id && (
                                    <div
                                      className="absolute z-50 mt-2 -translate-x-1/3 bg-white shadow-md p-2 rounded-md w-32"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                      }}
                                    >
                                      <MultiDatesPicker
                                        id="schedule"
                                        title="Schedule"
                                        onChange={(field: string, dates: string[]) => handleDateChange?.(row.id, dates)}
                                        data={dateData}
                                      />
                                    </div>
                                  )}
                                </div>
                              ) : column.format === TableDataType.DROPDOWN ? (
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                  }}
                                >
                                  <DropdownMenu
                                    disableEmptyOption
                                    hideEmptyOption
                                    options={column.dropdownOptions}
                                    value={rawValue}
                                    onChange={(ev: ChangeEvent<HTMLSelectElement>) => {
                                      ev.stopPropagation()
                                      ev.preventDefault()
                                      const newValue = ev.target.value
                                      handleDropdownChange?.(row, column, newValue)
                                      updateRowField(row.id, column.value, newValue)
                                    }}
                                    rounded
                                  />
                                </div>
                              ) : column.value === "gameSessionId" ? (
                                formatTableGameSessions(gameSessions, currency)
                              ) : (
                                formatTableCell(rawValue, column.format)
                              )}
                            </td>
                          )
                        })}
                      {actions && actions.length > 0 && (
                        <td>
                          <div className="flex gap-2 whitespace-nowrap">
                            {actions.map(
                              ({ name, condition, handler, buttonColor }) =>
                                (condition ? condition(row) : true) && (
                                  <button
                                    className={`btn btn-sm ${
                                      buttonColor
                                        ? "bg-[#ce4e79] text-white border-transparent hover:border-transparent hover:bg-[#ce4e79c5]"
                                        : "bg-[rgba(169,78,206,1)] text-white border-transparent hover:border-transparent hover:bg-[rgba(169,78,206,0.7)]"
                                    } modal-button`}
                                    onClick={(e) => onAction(e, row, handler)}
                                    key={name}
                                  >
                                    {name}
                                  </button>
                                )
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <TablePagination page={page} totalPages={totalPages} onJump={onJump} />
        </div>
      ) : loadError ? (
        <div className="w-full rounded-md flex flex-col items-center justify-center gap-3 p-6 text-lg text-black dark:text-white bg-grey-light-2 dark:bg-grey-med-2">
          <span>{loadError === "unavailable" ? t("table.loadFailedUnavailable") : t("table.loadFailed")}</span>
          <button
            className="btn btn-sm bg-[rgba(169,78,206,1)] text-white border-transparent hover:border-transparent hover:bg-[rgba(169,78,206,0.7)]"
            onClick={() => onLoad(page, pageSize)}
            disabled={loading}
          >
            {t("common.retry")}
          </button>
        </div>
      ) : (
        <NoData />
      )}
    </div>
  )
}
